import { AxiosError } from 'axios';
import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';
import { useGet } from 'hooks/use-get';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useQueryClient, UseQueryResult } from '@tanstack/react-query';
import { useLocation, useParams } from 'react-router-dom';
import {
  getR2Calibration,
  getBluViuNutritionCalibrationStatus,
} from 'views/bluviu-nutrition/features/calibrations/api/r2-calibration';
import { getSimpleCalibration, getSimpleCalibrationStatus } from 'views/bluviu-nutrition/features/calibrations/api/simple-calibration';
import { CalibrationsParams } from '../types/calibrations-params.type';
import { R2Calibration } from '../types/r2-calibration.type';
import { SimpleCalibration } from '../types/simple-calibration.type';

type State = {
  simpleCalibrationQuery: UseQueryResult<SimpleCalibration, AxiosError<unknown, any>>;
  r2CalibrationQuery: UseQueryResult<R2Calibration, AxiosError<unknown, any>>;
  simpleCalibrationStatusQuery: UseQueryResult<{ calibrationIsRunning: boolean }, AxiosError<unknown, any>>;
  r2CalibrationStatusQuery: UseQueryResult<{ calibrationIsRunning: boolean }, AxiosError<unknown, any>>;
};

const CalibrationContext = React.createContext<State | undefined>(undefined);

type CalibrationProviderProps = {
  children: React.ReactNode;
};

const CalibrationProvider: React.FC<CalibrationProviderProps> = ({ children }) => {
  const params = useParams<CalibrationsParams>();
  const location = useLocation();
  const queryClient = useQueryClient();
  const snackbarDispatch = useSnackbarDispatch();

  const [simpleCalibrationRefetchInterval, setSimpleCalibrationRefetchInterval] = useState<number | false | undefined>(false);
  const [r2CalibrationRefetchInterval, setR2CalibrationRefetchInterval] = useState<number | false | undefined>(false);
  const previousSimpleCalibrationRunning = useRef<{ key: string; isRunning: boolean }>();
  const previousR2CalibrationRunning = useRef<{ key: string; isRunning: boolean }>();

  const simpleCalibrationQuery = useGet(
    ['getSimpleCalibration', params.globalSiteId, params.calibrationId],
    ({ queryKey }) => getSimpleCalibration(String(queryKey[1]), String(queryKey[2])),
    {
      enabled: location.pathname.includes('simple'), // is there a better way?
    }
  );


  const r2CalibrationQuery = useGet(
    ['getR2Calibration', params.globalSiteId, params.calibrationId],
    ({ queryKey }) => getR2Calibration(String(queryKey[1]), String(queryKey[2])),
    {
      enabled: location.pathname.includes('multi'), // is there a better way?
    }
  );

  const simpleCalibrationStatusQuery = useGet(
    ['getSimpleCalibrationStatus', params.globalSiteId, params.calibrationId],
    ({ queryKey }) => getSimpleCalibrationStatus(String(queryKey[1]), String(queryKey[2])),
    {
      enabled: !!simpleCalibrationRefetchInterval,
      refetchInterval: simpleCalibrationRefetchInterval,
    }
  );
  const r2CalibrationStatusQuery = useGet(
    ['getBluViuNutritionCalibrationStatus', params.globalSiteId, params.calibrationId],
    ({ queryKey }) => getBluViuNutritionCalibrationStatus(String(queryKey[1]), String(queryKey[2])),
    {
      enabled: !!r2CalibrationRefetchInterval,
      refetchInterval: r2CalibrationRefetchInterval,
    }
  );
  useEffect(() => {
    const status = simpleCalibrationStatusQuery.data;
    if (!status) return;

    const statusKey = `${params.globalSiteId}:${params.calibrationId}`;
    const previousStatus = previousSimpleCalibrationRunning.current;

    setSimpleCalibrationRefetchInterval(status.calibrationIsRunning ? 3000 : false);

    if (previousStatus?.key === statusKey && previousStatus.isRunning && !status.calibrationIsRunning) {
      queryClient.invalidateQueries({
        queryKey: ['getSimpleCalibration', params.globalSiteId, params.calibrationId],
      });

      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: 'Simple calibration has completed!' },
      });
    }

    previousSimpleCalibrationRunning.current = { key: statusKey, isRunning: status.calibrationIsRunning };
  }, [params.calibrationId, params.globalSiteId, queryClient, simpleCalibrationStatusQuery.data, snackbarDispatch]);

  useEffect(() => {
    const status = r2CalibrationStatusQuery.data;
    if (!status) return;

    const statusKey = `${params.globalSiteId}:${params.calibrationId}`;
    const previousStatus = previousR2CalibrationRunning.current;

    setR2CalibrationRefetchInterval(status.calibrationIsRunning ? 3000 : false);

    if (previousStatus?.key === statusKey && previousStatus.isRunning && !status.calibrationIsRunning) {
      queryClient.invalidateQueries({
        queryKey: ['getR2Calibration', params.globalSiteId, params.calibrationId],
      });

      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: 'Multi cage calibration has completed!' },
      });
    }

    previousR2CalibrationRunning.current = { key: statusKey, isRunning: status.calibrationIsRunning };
  }, [r2CalibrationStatusQuery.data, params.calibrationId, params.globalSiteId, queryClient, snackbarDispatch]);
  return (
    <CalibrationContext.Provider
      value={{ simpleCalibrationQuery, r2CalibrationQuery, simpleCalibrationStatusQuery, r2CalibrationStatusQuery }}>
      {children}
    </CalibrationContext.Provider>
  );
};

function useCalibrationState() {
  const context = useContext(CalibrationContext);
  if (context === undefined) {
    throw new Error('useCalibrationState must be used within a CalibrationProvider');
  }
  return context;
}

export { CalibrationProvider, useCalibrationState };
